import { Box, useTheme } from "@mui/material";
import { MprcInfiniteLoading, MprcRequestErrorReport } from "components";
import { MprcFilterNoRecords } from "components/filter/MprcFilterNoRecords";
import { CoreFilterCommand, FeedAuthorSelectionType, FeedType, KnowledgeFeedOrder, UserProfileDto } from "generated/CoreApi_v1";
import { usePageTitle } from "hooks/usePageTitle";
import { useWindowSize } from "hooks/useWindowSize";
import { useInsideLayout } from "layouts/common/inside/InsideLayoutContext";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { BaseComponentHeader } from "views/common/components/BaseComponentHeader";
import { EditUser } from "views/foundation/user/Home/EditUser";
import { FeedContainer } from "views/knowledge/feed/components/FeedContainer";
import { IFeedFilterOptions } from "views/knowledge/feed/components/FeedFilter";
import { getClearFilter, getFilter } from "views/knowledge/feed/components/FeedFilter/FeedFilterCommon";
import { FeedManager, FeedManagerHandle } from "views/knowledge/feed/components/FeedManager";
import { FeedCustomEventProps, subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { IOpenForm } from "views/knowledge/feed/FeedUserProfile";
import { FeedUserProfileFilter } from "views/knowledge/feed/FeedUserProfile/FeedUserProfileFilter";
import { FeedUserProfileHeader } from "views/knowledge/feed/FeedUserProfile/FeedUserProfileHeader";
import { FeedUserProfileRightPanel } from "views/knowledge/feed/FeedUserProfile/FeedUserProfileRightPanel";
import { OutsideRightPanel } from "views/knowledge/publication/pages/components/OutsideRightPanel";

interface IProps {
    userId: string;
    isHome?: boolean;
    isKnowledge?: boolean;
    breakpoint: "md" | "lg";
}

interface IPrevValues {
    expandedAdvFilter: boolean;
    selection: FeedAuthorSelectionType;
}

/**
 * Feed do Perfil do Usuário.
 * @param props
 */
export const FeedUserProfileBase: React.FC<IProps> = (props) => {
    const { userId: id, isHome, isKnowledge, breakpoint } = props;

    const api = useCoreApi();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const layout = useInsideLayout();

    const { width } = useWindowSize({ wait: 50 });
    const { t } = useTranslation("knowledge");

    const [count, setCount] = useState<number | undefined | null>(undefined);
    const [viewError, setViewError] = useState<any>();
    const [userId, setUserId] = useState<string | undefined>();
    const [user, setUser] = useState<UserProfileDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [prevPub, setPrevPub] = useState<string | null>(location.state?.publicationId ?? null);
    const [expandedAdvFilter, setExpandedAdvFilter] = useState<boolean>(location.state?.prevValues?.expandedAdvFilter ?? false);
    const [showPanel, setShowPanel] = useState<boolean>(true);
    const [openForm, setOpenForm] = useState<IOpenForm | undefined>(undefined);
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);

    const feedRef = useRef<FeedManagerHandle>(undefined!);
    const selection = useRef<FeedAuthorSelectionType>(location.state?.prevValues?.selection ?? FeedAuthorSelectionType.Publication);

    usePageTitle(t("Feed.UserProfile.PageTitle"));

    const prevValues = useRef<IPrevValues>({
        expandedAdvFilter: location.state?.prevValues?.expandedAdvFilter ?? false,
        selection: location.state?.prevValues?.selection ?? FeedAuthorSelectionType.Publication
    });

    const viewFilter = useRef<IFeedFilterOptions>(
        location.state?.filter
        ?? {
            searchText: "",
            advFilters: [],
            order: isHome ? KnowledgeFeedOrder.Latest : KnowledgeFeedOrder.MostRelevant
        } as IFeedFilterOptions
    );

    const isFiltered = viewFilter.current.advFilters.length > 0 || (viewFilter.current.searchText != undefined && viewFilter.current.searchText.length > 0);

    ///
    const handleAdvFilterChange = useCallback((filter: IFeedFilterOptions, selectionType: FeedAuthorSelectionType) => {
        viewFilter.current = filter;
        viewFilter.current.ftsTitleAuthor = filter.advFilters.find(p => p.command == CoreFilterCommand.KnowledgeContent) == null;
        selection.current = selectionType;
        setCount(undefined);
        setPrevPub(null);
    }, [userId]);

    ///
    const onGetData = async (startIndex: number, endIndex: number) => {
        const filters = getFilter(viewFilter.current);

        var result = await api.v1.knowledgeFeed.getFeedUserProfile({
            userId: user!.userId,
            startIndex,
            endIndex,
            filters,
            startPublicationId: location.state?.publicationId ?? null,
            order: viewFilter.current.order,
            searchText: viewFilter.current.searchText ?? null,
            selectionType: selection.current
        });
        setCount(result.data.totalRows);
        return result.data.items;
    };

    ///
    const load = () => {
        setLoading(true);
        setViewError(null);

        if (userId) {
            api.v1.knowledgeFeed.getFeedUserProfileHeader({ UserId: userId! })
                .then((result) => {
                    setUser(result.data);
                })
                .catch((error: any) => {
                    setViewError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    ///
    const handleReset = (user?: UserProfileDto) => {
        if (user) {
            setUser(user);
        }
        else if (userId) {
            load();
        }
    };

    ///
    const handleEdit = (user: IOpenForm) => {
        setOpenForm(user);
    };

    ///
    const refreshData = (e: FeedCustomEventProps) => {
        if (e.detail == userId && viewFilter.current.searchText == undefined && viewFilter.current.advFilters.length == 0 && viewFilter.current.order == KnowledgeFeedOrder.Latest && !feedRef.current.scrolled()) {
            feedRef.current.reset();
            setCount(undefined);
        }
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        if (userId) {
            load();
        }

        subscribe("AuthorNewPublication", refreshData);
        return () => {
            unsubscribe("AuthorNewPublication", refreshData);
        };
    }, [userId]);

    ///
    useEffect(() => {
        feedRef.current?.reset();
    }, [user]);

    ///
    useEffect(() => {
        if (count == null && prevPub == null) {
            navigate(document.location.search, { replace: true, state: { filter: viewFilter.current, prevValues: { expandedAdvFilter, selection: selection.current } } });
        }
    }, [count]);

    ///
    useEffect(() => {
        if (prevPub != location.state?.publicationId) {
            setPrevPub(null);
        }
        else if (userId && userId == id && prevValues.current.expandedAdvFilter == expandedAdvFilter) {
            feedRef.current?.reset();
        }
    }, [location.state]);

    ///
    useEffect(() => {
        prevValues.current = {
            expandedAdvFilter,
            selection: selection.current
        };
    }, [expandedAdvFilter, selection]);

    ///
    useEffect(() => {
        if (isKnowledge) layout.changeSubHeaderText(t("Feed.UserProfile.HeaderTitle"));

        setUserId(id);
        setPrevPub(location.state?.publicationId ?? null);
        viewFilter.current = location.state?.filter
            ?? {
                searchText: "",
                advFilters: [],
                order: isHome ? KnowledgeFeedOrder.Latest : KnowledgeFeedOrder.MostRelevant
            } as IFeedFilterOptions;
    }, [document.location.pathname]);

    useEffect(() => {
        if (openRightPanel && width > theme.breakpoints.values[breakpoint])
            setOpenRightPanel(false);
    }, [width]);

    const showRightPanel = useMemo(() => {
        return width < theme.breakpoints.values[breakpoint];
    }, [width]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    return openForm
        ? <EditUser
            userId={openForm?.userId}
            onClose={() => setOpenForm(undefined)}
        />
        : viewError
            ? <MprcRequestErrorReport error={viewError} onRetry={() => load()} />
            : loading
                ? <MprcInfiniteLoading visible />
                : (
                    <>
                        {
                            location.pathname != RootPaths.SelfProfile && !isHome &&
                            <BaseComponentHeader
                                title={t("Feed.UserProfile.HeaderTitle")}
                                onBack={() => navigate(-1)}
                            />
                        }

                        <FeedUserProfileHeader
                            user={user ?? null}
                            onRigthPanelButtonClick={showRightPanel ? () => setOpenRightPanel(state => !state) : undefined}
                            onReset={handleReset}
                            onEdit={handleEdit}
                        />

                        <Box mt={2} mb={2}>
                            <FeedUserProfileFilter
                                user={user!}
                                count={count ?? undefined}
                                filter={viewFilter.current}
                                expandedAdvFilter={expandedAdvFilter}
                                selectionType={selection.current}
                                onFilterChange={handleAdvFilterChange}
                                onError={(error) => setViewError(error)}
                                onSelectedExpandedAdvFilter={(selected) => {
                                    setExpandedAdvFilter(selected);
                                    navigate(document.location.search, { replace: true, state: { prevValues: { ...prevValues, expandedAdvFilter: selected } } });
                                }}
                                isFiltered={isFiltered}
                            />
                        </Box>

                        <FeedContainer
                            rightPanelBreakpoint={breakpoint}
                            feed={
                                <div>
                                    {count == 0 &&
                                        <MprcFilterNoRecords
                                            message={selection.current == FeedAuthorSelectionType.Publication
                                                ? t("Feed.UserProfile.NotFoundPublicationMessage")
                                                : t("Feed.UserProfile.NotFoundMentionMessage")
                                            }
                                            filtered={isFiltered}
                                            onResetFilter={() => handleAdvFilterChange(getClearFilter(), selection.current)}
                                        />
                                    }
                                    <FeedManager
                                        feedType={FeedType.UserProfile}
                                        otherInfo={{ feedType: FeedType.UserProfile }}
                                        onGetData={onGetData}
                                        ref={feedRef}
                                        useInfiniteLoading
                                        previousPublicationId={prevPub}
                                        onError={(error) => setViewError(error)}
                                        onGobackTop={() => {
                                            setCount(null);
                                            setPrevPub(null);
                                        }}
                                    />
                                </div>
                            }
                            moreInfo={
                                userId && user && showPanel
                                    ? <FeedUserProfileRightPanel key="FeedUserProfileRightPanel" user={user!} onEmptyPanel={() => setShowPanel(false)} />
                                    : undefined
                            }
                            marginRightPanel={0}
                        />

                        <OutsideRightPanel
                            open={openRightPanel}
                            onClose={() => setOpenRightPanel(false)}
                            content={
                                <FeedUserProfileRightPanel
                                    renderMode="drawer"
                                    user={user!}
                                    onEmptyPanel={() => setOpenRightPanel(false)}
                                />
                            }
                        />
                    </>
                );
};
